/**
 * Ahrefs Web Analytics — custom events via window.AhrefsAnalytics.sendEvent
 * Fires for the same consent-gated product events as GA4 and Algolia Insights.
 */

import type { AnalyticsEvent } from '../../../types/analytics-events';
import type { AnalyticsEventParams } from '../../../types/analytics';
import { isClientAnalyticsAllowed } from '../../cookie-consent/client-analytics-allowed';
import { isAhrefsExcludedBotUserAgent } from './ahrefs-excluded-bots';
import { mapParamsToGA4 } from './ga4';

declare global {
  interface Window {
    AhrefsAnalytics?: {
      sendEvent: (name: string, options?: { props?: Record<string, string> }) => void;
    };
  }
}

const MAX_PROP_VALUE_LEN = 1024;

/** Ahrefs disallows dashes in property names; normalize to underscores. */
export function sanitizeAhrefsPropKey(key: string): string {
  return key.replace(/-/g, '_');
}

export function stringifyValueForAhrefs(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return undefined;
    return String(value);
  }
  if (typeof value === 'string') {
    const t = value.trim();
    return t.length === 0 ? undefined : t.slice(0, MAX_PROP_VALUE_LEN);
  }
  try {
    return JSON.stringify(value).slice(0, MAX_PROP_VALUE_LEN);
  } catch {
    return undefined;
  }
}

export function flatRecordToAhrefsProps(record: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [rawKey, rawVal] of Object.entries(record)) {
    const key = sanitizeAhrefsPropKey(rawKey);
    if (!key) continue;
    const s = stringifyValueForAhrefs(rawVal);
    if (s !== undefined) out[key] = s;
  }
  return out;
}

function legacyAnalyticsParamsToFlatPayload(params: AnalyticsEventParams): Record<string, unknown> {
  return {
    event_category: params.category,
    ...(params.label != null ? { event_label: params.label } : {}),
    ...(params.value !== undefined ? { value: params.value } : {}),
    ...(params.non_interaction !== undefined ? { non_interaction: params.non_interaction } : {}),
    ...(params.query ? { search_term: params.query } : {}),
    ...(params.filter_key ? { filter_key: params.filter_key } : {}),
    ...(params.filter_value ? { filter_value: params.filter_value } : {}),
    ...(params.sort_type ? { sort_type: params.sort_type } : {}),
    ...(params.page_number !== undefined ? { page_number: params.page_number } : {}),
    ...(params.hits_count !== undefined ? { hits_count: params.hits_count } : {}),
    ...(params.job_id ? { job_id: params.job_id } : {}),
    ...(params.company_id ? { company_id: params.company_id } : {}),
    ...(params.job_title ? { job_title: params.job_title } : {}),
    // Not `source` — see the matching comment in analytics.ts's legacy GA4 mapper: GA4 treats
    // that key as campaign attribution regardless of event name, and Ahrefs is kept in sync.
    ...(params.source ? { click_source: params.source } : {}),
    ...(params.locale ? { locale: params.locale } : {}),
    ...(params.url ? { url: params.url } : {}),
  };
}

function sendAhrefsEvent(name: string, props: Record<string, string>): boolean {
  if (typeof window === 'undefined' || !isClientAnalyticsAllowed()) return false;
  if (isAhrefsExcludedBotUserAgent(window.navigator?.userAgent)) return false;
  const api = window.AhrefsAnalytics?.sendEvent;
  if (!api) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Ahrefs Analytics] AhrefsAnalytics.sendEvent not available for:', name);
    }
    return false;
  }
  const trimmed = name.trim();
  if (!trimmed) return false;
  try {
    api(trimmed, { props });
    return true;
  } catch (e) {
    console.error('[Ahrefs Analytics] sendEvent failed:', e);
    return false;
  }
}

/**
 * Ahrefs' own script hardcodes the literal event name "pageview" (no underscore) for its
 * native auto-detected pageview beacon (fired on history.pushState / initial load — see
 * analytics.ahrefs.com/analytics.js), and its dashboard Views/Bounce Rate metrics are keyed
 * off that exact string. Our typed `page_view` event follows GA4's naming convention instead.
 *
 * Only translate it when `ahrefs_send_as_pageview` is set (SRP filter/sort/query/page/map
 * changes — see contexts/SearchContext.tsx), which fire via history.replaceState and are
 * therefore invisible to Ahrefs' own pushState-based auto-tracking. Real Vike navigations
 * (job detail, company page, locale switch, etc.) call history.pushState — Vike does this
 * specifically so third-party trackers can observe it — so Ahrefs already auto-counts those
 * natively before our own `spa_page_view` event even fires; translating that one too would send a
 * second "pageview" for the same navigation and double-count Ahrefs' Views metric.
 */
function mapEventNameToAhrefs(event: AnalyticsEvent): string {
  if (event.name === 'spa_page_view' && event.params.ahrefs_send_as_pageview === true) return 'pageview';
  return event.name;
}

/**
 * Typed analytics events — event name matches {@link AnalyticsEvent} `name` (same actions as GA/Algolia pipeline),
 * except `spa_page_view` events explicitly marked `ahrefs_send_as_pageview`, translated to Ahrefs' own
 * "pageview" sentinel (see {@link mapEventNameToAhrefs}).
 */
export function sendAhrefsWebAnalyticsTypedEvent(event: AnalyticsEvent): boolean {
  const gaShape = mapParamsToGA4(event);
  const props = flatRecordToAhrefsProps(gaShape as Record<string, unknown>);
  return sendAhrefsEvent(mapEventNameToAhrefs(event), props);
}

/** Raw event by name/flat payload, for events outside the typed union (e.g. debounced `search`). */
export function sendAhrefsWebAnalyticsRawEvent(
  name: string,
  payload: Record<string, unknown>,
): boolean {
  return sendAhrefsEvent(name, flatRecordToAhrefsProps(payload));
}

/** Legacy {@link AnalyticsEventParams} shape (gtag-compatible payload flattened to Ahrefs props). */
export function sendAhrefsWebAnalyticsLegacyEvent(params: AnalyticsEventParams): boolean {
  const action = params.action?.trim();
  if (!action) return false;
  const flat = legacyAnalyticsParamsToFlatPayload(params);
  const props = flatRecordToAhrefsProps(flat);
  return sendAhrefsEvent(action, props);
}

/**
 * Mirrors the GA4 job-apply event (`job_apply_conversion` for real CTA clicks,
 * `job_application_logged` for tracker bookkeeping) as an Ahrefs custom event.
 */
export function sendAhrefsJobApplyEvents(payload: {
  eventName: string;
  jobId: string;
  jobTitle: string;
  companyId: string;
  applySource: string;
  method?: string;
  locale?: string;
  queryID: string | null;
}): boolean {
  if (typeof window === 'undefined' || !isClientAnalyticsAllowed()) return false;
  const props = flatRecordToAhrefsProps({
    category: 'conversion',
    apply_source: payload.applySource,
    value: 1,
    job_id: payload.jobId,
    job_title: payload.jobTitle,
    company_id: payload.companyId,
    method: payload.method || 'external_link',
    ...(payload.locale ? { locale: payload.locale } : {}),
    ...(payload.queryID ? { query_id: payload.queryID } : {}),
  });
  return sendAhrefsEvent(payload.eventName, props);
}
